import React, { useMemo } from 'react';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Tag } from '@frontend/app/refresh-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis, faPaintbrush } from '@fortawesome/free-solid-svg-icons';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import { ProjectByIdQuery_project_onboardingTemplates } from '@frontend/app/queries/types/ProjectByIdQuery';
import { ProgramOnboardingTemplateType } from '@frontend/app/types/globalTypes';
import { useHistory } from 'react-router-dom';
import { logger } from '@common';
import { useDeleteProgramOnboardingTemplate } from '../hooks/useDeleteProgramOnboardingTemplate';
import { useDuplicateProgramOnboardingTemplate } from '../hooks/useDuplicateProgramOnboardingTemplate';
import { SettingsRoutes } from '../../../constants';
import { useUpdateProgramOnboardingTemplate } from '../hooks/useUpdateProgramOnboardingTemplate';

const amplifyTemplate = 'https://storage.googleapis.com/aspirex-static-files/onboarding-template/amplifyTemplate.png';
const pulseTemplate = 'https://storage.googleapis.com/aspirex-static-files/onboarding-template/pulseTemplate.png';

interface ApplicationProps {
  application: ProjectByIdQuery_project_onboardingTemplates;
  onUpdate: () => void;
  projectId: number;
}

export function Application(props: ApplicationProps) {
  const { application, onUpdate, projectId } = props;
  const history = useHistory();
  const [deleteProgramOnboardingTemplate] = useDeleteProgramOnboardingTemplate();
  const [duplicateProgramOnboardingTemplate] = useDuplicateProgramOnboardingTemplate();
  const [updateProgramOnboardingTemplate] = useUpdateProgramOnboardingTemplate();

  const timeRange = useMemo(() => {
    if (!application.startDate) {
      return '';
    }
    const startDate = new Date(application.startDate);
    const endDate = new Date(application.endDate);

    if (application.startDate && !application.endDate) {
      return `${startDate.toLocaleDateString()} - No end date`;
    }

    return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
  }, [application]);

  const onClickDeleteApplication = async () => {
    try {
      await deleteProgramOnboardingTemplate({
        variables: {
          id: application.id,
        },
      });
    } catch (error) {
      logger.error('Error deleting program onboarding template:', error);
    } finally {
      onUpdate();
    }
  };

  const onClickDuplicateApplication = async () => {
    try {
      await duplicateProgramOnboardingTemplate({
        variables: {
           projectId,
          templateId: application.id,
        },
      });
    } catch (error) {
      console.error('Error duplicating program onboarding template:', error);
    } finally {
      onUpdate();
    }
  };

  const onClickViewApplication = () => {
    history.push(SettingsRoutes.recruiting.template.replace(':projectId', projectId.toString()).replace(':templateId', application.id.toString()));
  };

  const onClickCustomizeApplication = () => {
    history.push(SettingsRoutes.recruiting.template.replace(':projectId', projectId.toString()).replace(':templateId', application.id.toString()));
  };

  const onClickDelistFromMarketplace = async () => {
    try {
      await updateProgramOnboardingTemplate({
        variables: {
          input: {
            id: application.id,
            data: {
              isPublished: false,
            },
          },
        },
      });
    } catch (error) {
      logger.error('Error delisting program onboarding template:', error);
    } finally {
      onUpdate();
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="flex p-4 justify-between items-center">
        {/* Left Content */}
        <div className="flex flex-col flex-1 gap-4">
          {/* Top Section */}
          <div className="flex flex-col gap-2">
            {application.isPublished && (
              <Tag
                label="Published to Creator Marketplace"
                bgColor="bg-green-2"
                textColor="text-green-6"
                className="w-fit"
              />
            )}
            <div className="flex flex-col gap-0">
              <h3 className="text-lg font-medium text-grey-6 m-0">{application.applicationTitle}</h3>
              <p className="text-sm text-grey-4 m-0">{timeRange}</p>
            </div>
          </div>

          {/* Bottom Section - Buttons */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-8 gap-2" onClick={onClickCustomizeApplication}>
              <FontAwesomeIcon icon={faPaintbrush} />
              Customize
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Open menu">
                  <FontAwesomeIcon icon={faEllipsis} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="text-sm" onClick={onClickViewApplication}>
                  View Application
                </DropdownMenuItem>
                <DropdownMenuItem className="text-sm" onClick={onClickDelistFromMarketplace}>Delist from Marketplace</DropdownMenuItem>
                <DropdownMenuItem className="text-sm" onClick={onClickDuplicateApplication}>
                  Duplicate
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-sm text-red-600 focus:bg-red-50" onClick={onClickDeleteApplication}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Right Content - Image Placeholder */}
        <div className="w-[130px] h-[130px] bg-grey-1 border border-grey-2 rounded-2xl flex items-center justify-center">
          {/* <Skeleton className="w-12 h-24 bg-grey-3 rounded" /> */}
          <img
            src={application.templateType === ProgramOnboardingTemplateType.AMPLIFY ? amplifyTemplate : pulseTemplate}
            alt={application.templateType}
            className="w-[48px] h-[96px] object-contain"
          />
        </div>
      </CardContent>
    </Card>
  );
}
